import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { injectIntl } from 'react-intl'
import Spinner from '../../../util/spinner-shim'
import {
  isNumber,
  fromC,
  round,
  fourToTwenty,
  displayFormula,
  rpmOrchardRiteAutometer9117,
  cellSignalToRssi,
  cellSignalToQuality,
  litersToUserPreference,
} from 'altrac-conversions'

import {
  IconLink,
  IconError,
  CellBars,
} from '../Navigation/Buttons'

import {
  SettingAutoModeAmarillo,
  SettingAutoModeHauff,
  SettingAutoModePump,
  SettingAutoModePump1,
  SettingAutoModePump2,
} from './SettingAutostart'

import {
  SettingModbusStart,
  SettingEngineState,
  SettingPumpState,
  SettingPumpState1,
  SettingPumpState2,
} from './SettingEngineState'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'

import styles from '../../../../css/devicePage.module.css'
import colors from '../../../../css/colors.module.css'
import Semios from '../Graphics/Semios'
import { WindMachine } from '../Graphics/WindMachine'

const TelemetryButtons = function TelemetryButtons (
  {
    device = {
      reading0: {},
      physical: {
        deviceType: 'Unknown',
        deviceNumber: 'XX',
        fuelTankSize: 0,
        fuelSensorRange: 0,
      },
    },
    status,
    statsLookup,
    statsReady,
    flowStats,
    flowReady,
    dailySummary,
    path,
    telemetryTypes = [
      'batteryInternal',
    ],
    customer,
    pinCode,
    intl,
    page,
    physical,
    tempConv = 'f',
    level = 0,
  }
) {
  const {
    formatMessage,
  } = intl

  const {
    locale: {
      unitsOfVolume = 'acreFeet',
      unitsOfFlow = 'gpm',
    } = {},
  } = AppStore.getUser() || {}

  const unitsOfFlowTitle = unitsOfFlow || 'gpm'

  const {
    physical: {
      rpmMultiplier,
      rpmSensor,
    },
    application_settings_new: {
      settings: {
        schedules: settingSchedules,
        timeblocks: settingTimeblocks,
        valveTime: valveSchedules,
      } = {},
      status: settingStatus = 'onDevice',
    } = {},
  } = device

  const telPath = path + 'telemetry/'

  const loadingLabel = formatMessage({
    id: 'sensor.calculating.state',
    defaultMessage: 'Loading…',
    description: 'Value displayed while data is loading',
  })

  const CALCULATING = (
    <div
      className={styles.tileLoader}
      role='status'
      aria-label={loadingLabel}
    >
      <Spinner name='double-bounce' fadeIn='none' />
    </div>
  )

  const STATS_NOT_LOADED = formatMessage({
    id: 'sensor.notLoaded.state',
    defaultMessage: 'not loaded',
    description: 'Value displayed when stats have not been loaded',
  })

  const telemetryReturn = []
  let unitFresh = ''
  let valueFresh = ''
  for (let i = 0; i < telemetryTypes.length; i += 1) {
    let telemetryTemporary

    const reading0 = device.reading0 || {}
    const reading1 = device.reading1 || {}
    if (typeof telemetryTypes[i] === 'object') {
      const {
        valueKey, statusKey = null, label = '',
        formula, multiplier, precision,
        thresholds, thresholdsColors, thresholdsValues,
      } = telemetryTypes[i]
      let { unit = '' } = telemetryTypes[i]
      let value = ''
      let color = colors.normal
      if (reading0[valueKey] === 0xFFFF || reading0[valueKey] === 6.5535) {
        value = 'ERR'
        unit = ''
      } else if (
        statusKey &&
        (reading0[statusKey] || reading0[statusKey] === 0) &&
        (((reading0[statusKey] & 0xFF) >>> 0) - 10) < 1
      ) {
        value = '-'
        unit = ''
      } else if (status.id === 'notConnected') {
        value = '-'
        unit = ''
      } else {
        value = displayFormula(
          formula,
          multiplier,
          precision,
          { tempConv, level },
          valueKey,
          reading0,
          reading1,
          device.physical || {}
        )
      }

      if (Array.isArray(thresholds)) {
        let thresholdNumber = thresholds.length - 1
        for (let j = 0; j < thresholds.length; j += 1) {
          if (value < thresholds[j]) {
            thresholdNumber = j - 1
            break
          }
        }
        if (
          Array.isArray(thresholdsValues) &&
          thresholdsValues.length === thresholds.length
        ) {
          value = thresholdsValues[thresholdNumber]
        }
        if (
          Array.isArray(thresholdsColors) &&
          thresholdsColors.length === thresholds.length
        ) {
          color = thresholdsColors[thresholdNumber]
          switch (thresholdsColors[thresholdNumber]) {
            case 'success':
              color = colors.success
              break
            case 'info':
              color = colors.info
              break
            case 'warning':
              color = colors.warning
              break
            case 'danger':
              color = colors.danger
              break
            case 'deviceProblem':
              color = colors.deviceProblem
              break
            case 'normal':
            default:
              color = colors.normal
          }
        }
      }

      const telemetryGenericPath = `${telPath}generic?valueKey=${valueKey}&multiplier=${multiplier}&formula=${formula}&label=${label}&precision=${precision}&stacked=false&thresholds=${thresholds}&thresholdsValues=${thresholdsValues}&thresholdsColors=${thresholdsColors}`

      telemetryTemporary = (
        <IconLink
          key={telemetryGenericPath}
          path={telemetryGenericPath}
          label={label}
          value={value}
          unit={unit}
          color={color}
        />
      )
    } else {
      let valueKey
      let value
      let label = ''
      let label2 = ''
      let label3 = ''
      let formula = ''
      let volumeTitle = 'Acre Feet'
      switch (unitsOfVolume) {
        case 'liters':
          volumeTitle = 'Liters'
          break
        case 'gallons':
          volumeTitle = 'Gallons'
          break
        case 'acreInch':
          volumeTitle = 'Acre Inch'
          break
        case 'acreFeet':
          volumeTitle = 'Acre Feet'
          break
        case 'cubicFeet':
          volumeTitle = 'Cubic Feet'
          break
        case 'hectareM':
          volumeTitle = 'Hectare Meter'
          break
        case 'cubicM':
          volumeTitle = 'Cubic Meter'
          break
        default:
          volumeTitle = 'Acre Feet'
      }
      switch (telemetryTypes[i]) {
        case 'temperature':
        {
          label = formatMessage({
            id: 'sensor.temperature.ambient.short',
            defaultMessage: 'Temperature',
            description: 'Temperature Label',
          })
          valueKey = 128
          value = reading0[valueKey]
          let tempBad = 0
          let temperature
          if (((!value && value !== 0) || Number.isNaN(value))) {
            // need to make red and put a temp symbol here
            tempBad = 2
          } else {
            temperature = fromC(value, tempConv, 1)
            unitFresh = '°'
            if (status.id === 'notConnected') {
              tempBad = 1
            } else if (temperature === null) {
              tempBad = 2
            }
          }
          if (tempBad === 1) {
            telemetryTemporary = (
              <IconLink
                key={`${telPath}${valueKey}${telemetryTypes[i]}`}
                name={`${telemetryTypes[i]}`}
                path={`${telPath}generic` +
                  `?valueKey=${valueKey}` +
                  '&multiplier=1' +
                  '&formula=temperature' +
                  `&label=${label.replace(' ', '+')}` +
                  '&precision=1'}
                label={`
                  ${label} (${tempConv.toUpperCase()})
                `}
                value='-'
                unit=''
              />
            )
          } else if (tempBad > 1) {
            telemetryTemporary = (
              <IconError
                key={`${telPath}${valueKey}${telemetryTypes[i]}`}
                name={`${telemetryTypes[i]}`}
                type='temperature'
                label={`
                  ${label} (${tempConv.toUpperCase()})
                `}
                path={`${telPath}generic` +
                  `?valueKey=${valueKey}` +
                  '&multiplier=1' +
                  '&formula=temperature' +
                  `&label=${label.replace(' ', '+')}` +
                  '&precision=1'}
              />
            )
          } else {
            telemetryTemporary = (
              <IconLink
                key={`${telPath}${valueKey}${telemetryTypes[i]}`}
                name={`${telemetryTypes[i]}`}
                path={`${telPath}generic` +
                  `?valueKey=${valueKey}` +
                  '&multiplier=1' +
                  '&formula=temperature' +
                  `&label=${label.replace(' ', '+')}` +
                  '&precision=1'}
                label={`
                  ${label} (${tempConv.toUpperCase()})
                `}
                value={temperature}
                unit={unitFresh}
              />
            )
          }
          break
        }
        case 'batteryInternal':
        {
          label = formatMessage({
            id: 'sensor.battery.internal',
            defaultMessage: 'Internal Battery',
            description: 'Internal Battery Label',
          })
          valueKey = 129
          const batteryInternalFresh = round(reading0[valueKey] * 100, 0)
          let batteryInternalColor = colors.normal
          unitFresh = '%'
          if (status.id === 'notConnected') {
            batteryInternalColor = colors.redForeground
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}129${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=129' +
                '&multiplier=0.01' +
                '&formula=default' +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={batteryInternalFresh}
              unit={unitFresh}
              color={batteryInternalColor}
            />
          )
          break
        }
        case 'windMachineDiagnostics':
          label = formatMessage({
            id: 'windmachine.diagnostics',
            defaultMessage: 'Diagnostics',
            description: 'Diagnostics Label',
          })
          valueKey = 134

          telemetryTemporary = (
            <IconLink
              key={`${telPath}134${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}windMachineDiagnostics` +
                '?valueKey=134'}
              label={label}
              unit={unitFresh}
              icon={<WindMachine />}
              iconOnly
            />
          )
          break
        case 'rpm':
        {
          label = formatMessage({
            id: 'sensor.rpm',
            defaultMessage: 'RPM',
            description: 'RPM Label',
          })
          valueKey = 134
          value = reading0[valueKey]
          let rpmFresh = round(value / (rpmMultiplier || 1), 0)
          formula = 'default'

          if (rpmSensor === 'OrchardRiteAutometer9117') {
            rpmFresh = round(rpmOrchardRiteAutometer9117(value / (rpmMultiplier || 1)), 0)
            formula = 'rpmOrchardRiteAutometer9117'
          }

          unitFresh = ''

          // a reading without key 134 divides undefined and renders `NaN`
          if (status.id === 'notConnected' || !isNumber(rpmFresh)) {
            rpmFresh = '-'
            unitFresh = ''
          }

          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=134' +
                `&multiplier=${rpmMultiplier || 1}` +
                `&formula=${formula}` +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={rpmFresh}
              unit={unitFresh}
            />
          )
          break
        }
        case 'fuelLevel':
        {
          label = formatMessage({
            id: 'sensor.fuelLevel',
            defaultMessage: 'Fuel Level',
            description: 'Fuel Level Label',
          })
          let fuelColor = colors.normal
          valueKey = 133
          valueFresh = displayFormula(
            'fuelLevel',
            0.01,
            0,
            null,
            valueKey,
            reading0,
            null,
            device.physical || {}
          )
          unitFresh = ''
          if (isNumber(valueFresh)) unitFresh = '%'
          if (valueFresh === 'Not Connected') {
            fuelColor = colors.disconnected
            valueFresh = 'NOT CON'
            telemetryTemporary = (
              <IconError
                key={`${telPath}133${telemetryTypes[i]}`}
                name={`${telemetryTypes[i]}`}
                type='fuelLevelNotConnected'
                label={label}
                path={`${telPath}generic` +
                  '?valueKey=133' +
                  '&multiplier=0.01' +
                  '&formula=fuelLevel' +
                  `&label=${label.replace(' ', '+')}`}
              />
            )
            break
          }
          if (valueFresh === 'Error High') {
            fuelColor = colors.warning
            valueFresh = 'ERR'
          }
          if (status.id === 'notConnected') {
            valueFresh = '-'
            unitFresh = ''
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}133${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=133' +
                '&multiplier=0.01' +
                '&formula=fuelLevel' +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={valueFresh}
              unit={unitFresh}
              color={fuelColor}
            />
          )
          break
        }
        case 'cellular':
        {
          label = formatMessage({
            id: 'sensor.cellularSignal',
            defaultMessage: 'Cell Signal',
            description: 'Cellular Label',
          })
          label2 = formatMessage({
            id: 'sensor.cellularRssi',
            defaultMessage: 'RSSI dB',
            description: 'Cellular RSSI Label',
          })
          label3 = formatMessage({
            id: 'sensor.cellularQuality',
            defaultMessage: 'Quality',
            description: 'Cellular Quality Label',
          })
          valueKey = 179
          valueFresh = `${cellSignalToRssi(reading0[valueKey])}/${cellSignalToQuality(reading0[valueKey])}`
          unitFresh = ''
          formula = 'cellSignalToRssi,cellSignalToQuality'
          let cellColor = colors.normal
          if (status.id === 'notConnected') {
            cellColor = colors.redForeground
          }
          telemetryTemporary = (
            <IconLink
              key={`${valueKey}RSSIQUAL`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic?valueKey=${valueKey},${valueKey}&multiplier=1,1&formula=cellSignalToRssi,cellSignalToQuality&label=${label2.replace(' ', '+')},${label3.replace(' ', '+')}&stacked=false`}
              label={label}
              value={valueFresh}
              unit={unitFresh}
              color={cellColor}
            />
          )
          break
        }
        case 'cellularRSSI':
          label = formatMessage({
            id: 'sensor.cellularRssi',
            defaultMessage: 'RSSI dB',
            description: 'Cellular RSSI Label',
          })
          valueKey = 179
          valueFresh = cellSignalToRssi(reading0[valueKey])
          unitFresh = ''
          formula = 'cellSignalToRssi'
          if (status.id === 'notConnected') {
            valueFresh = '-'
            unitFresh = ''
          }
          telemetryTemporary = (
            <IconLink
              key='179RSSI'
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=179' +
                '&multiplier=1' +
                `&formula=${formula}` +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={valueFresh}
              unit={unitFresh}
            />
          )
          break
        case 'cellularSignalToBars':
        {
          valueKey = 179
          const signal = displayFormula(
            telemetryTypes[i],
            1,
            0,
            { tempConv, level },
            valueKey,
            reading0,
            reading1,
            device.physical || {}
          )

          label = formatMessage({
            id: 'sensor.signalStrength',
            defaultMessage: 'Signal Strength',
            description: 'Signal Strength Label',
          })
          label2 = formatMessage({
            id: 'sensor.cellularRssi',
            defaultMessage: 'RSSI dB',
            description: 'Cellular RSSI Label',
          })
          label3 = formatMessage({
            id: 'sensor.cellularQuality',
            defaultMessage: 'Quality',
            description: 'Cellular Quality Label',
          })

          unitFresh = ''
          formula = 'cellSignalToRssi'
          telemetryTemporary = (
            <CellBars
              key='179RSSI'
              path={`${telPath}generic?valueKey=${valueKey},${valueKey}&multiplier=1,1&formula=cellSignalToRssi,cellSignalToQuality&label=${label2.replace(' ', '+')},${label3.replace(' ', '+')}&stacked=false`}
              label={label}
              name='CellularSignalToBars'
              signalStrength={signal}
              unit={unitFresh}
            />
          )
          break
        }
        case 'cellularQuality':
          label = formatMessage({
            id: 'sensor.cellularQuality',
            defaultMessage: 'Quality',
            description: 'Cellular Quality Label',
          })
          valueKey = 179
          valueFresh = cellSignalToQuality(reading0[valueKey])
          formula = 'cellSignalToQuality'
          unitFresh = ''
          if (status.id === 'notConnected') {
            valueFresh = '-'
            unitFresh = ''
          }
          telemetryTemporary = (
            <IconLink
              key='179QUAL'
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=179' +
                '&multiplier=1' +
                `&formula=${formula}` +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={valueFresh}
              unit={unitFresh}
            />
          )
          break
        case 'batteryExternal':
        {
          label = formatMessage({
            id: 'sensor.battery.external',
            defaultMessage: 'Battery Voltage',
            description: 'Battery Voltage Label',
          })
          valueKey = 130
          const batteryExternalWarning = device.physical.batteryExternalWarning || 11.8
          const batteryExternalDead = device.physical.batteryExternalDead || 8
          let batteryExternal = displayFormula(
            'percentTo20V',
            1,
            1,
            null,
            valueKey,
            reading0,
            null,
            device.physical
          )
          let batteryExternalColor = colors.normal
          unitFresh = 'v'
          if (status.id === 'notConnected') {
            unitFresh = 'v'
            batteryExternalColor = colors.redForeground
          } else if (!isNumber(batteryExternal) || batteryExternal > 42) {
            // displayFormula returns 'ERR' for a missing or sentinel value; test
            // it before the thresholds, which a string silently falls through
            batteryExternal = 'ERR'
            unitFresh = ''
            batteryExternalColor = colors.redForeground
          } else if (batteryExternal <= batteryExternalDead) {
            batteryExternalColor = colors.danger
          } else if (batteryExternal <= batteryExternalWarning) {
            batteryExternalColor = colors.warning
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=130' +
                '&multiplier=1' +
                '&precision=2' +
                '&formula=percentTo20V' +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={batteryExternal}
              unit={unitFresh}
              color={batteryExternalColor}
            />
          )
          break
        }
        case 'pressure':
        {
          label = formatMessage({
            id: 'sensor.pressure',
            defaultMessage: 'Pressure',
            description: 'Pressure Label',
          })
          const pressureWarning = device.physical.pressureWarning || 100
          valueKey = 133
          let pressure = fourToTwenty(
            reading0[valueKey],
            device.physical.min || 0,
            device.physical.max || 100,
            device.physical.zero || 0,
            device.physical.precision || 0
          )
          let pressureColor = colors.normal
          unitFresh = ''
          if (pressure === 'ERH' || pressure === 'OFF') {
            telemetryTemporary = (
              <IconError
                key={`${telPath}${valueKey}${telemetryTypes[i]}`}
                name={`${telemetryTypes[i]}`}
                type='pressure'
                label={label}
                path={`${telPath}generic` +
                  '?valueKey=133' +
                  '&multiplier=1' +
                  '&formula=fourToTwenty' +
                  `&label=${label.replace(' ', '+')}` +
                  '&precision=1'}
              />
            )
            break
          }

          if (status.id === 'notConnected') {
            pressure = '-'
            unitFresh = ''
          } else if (pressure >= pressureWarning) {
            pressureColor = colors.danger
            unitFresh = ''
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=133' +
                '&multiplier=1' +
                '&formula=fourToTwenty' +
                `&label=${label.replace(' ', '+')}` +
                '&precision=1'}
              label={label}
              value={pressure}
              unit={unitFresh}
              color={pressureColor}
            />
          )
          break
        }
        case 'inputPower':
          label = formatMessage({
            id: 'sensor.powerState',
            defaultMessage: 'Power to Altrac',
            description: 'Power to Altrac Label',
          })
          valueKey = 186
          valueFresh = (reading0[valueKey] & 0x4) ? 'ON' : 'OFF'
          unitFresh = ''

          if (status.id === 'notConnected') {
            valueFresh = '-'
            unitFresh = ''
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}186${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=186' +
                '&multiplier=1' +
                '&formula=percentToDigitalString' +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={valueFresh}
              unit={unitFresh}
            />
          )
          break
        case 'flowMeterDaily':
          label = formatMessage({
            id: 'sensor.flow.totalAcreFeetDaily',
            defaultMessage: `Today's Total (${volumeTitle})`,
            description: 'Total Acre Feet Label',
          })
          valueKey = 'flowMeterSum'
          // hash = `${reading0.address},${valueKey},${moment().startOf('day').toISOString()},${moment().add(1, 'day').startOf('day').toISOString()}`
          valueFresh = CALCULATING
          if (typeof flowReady === 'undefined' || flowReady === null) {
            valueFresh = STATS_NOT_LOADED
          } else if (typeof flowReady === 'string' && flowReady === 'notLoaded') {
            valueFresh = CALCULATING
          } else if (typeof flowReady === 'string' && flowReady === 'loading') {
            valueFresh = CALCULATING
          } else if (
            typeof flowReady === 'string' && flowReady === 'loaded'
          ) {
            const currentData = dailySummary.slice(-1)
            if (dailySummary.length > 0) {
              valueFresh = litersToUserPreference(currentData[0].flow_cumulative, unitsOfVolume, 'volume', 2)
            }
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              // path={`${telPath}flow`
              //   + '?statType=todayTotal'
              //   + `&valueKey=${valueKey}`
              //   + `&multiplier=${pulseMultiplier}`
              //   + '&precision=2'
              //   + '&period=7'
              //   + '&formula=litersToAcreFeet'
              //   + '&label='}
              label={label}
              value={valueFresh}
              unit=''
            />
          )
          break
        case 'gallonsPerMin':
          label = formatMessage({
            id: 'sensor.flow.gpm',
            defaultMessage: `Flow Rate (${unitsOfFlowTitle})`,
            description: 'Flow Rate Label',
          })
          valueKey = 'flow'
          // hash = `${reading0.address},${valueKey},${moment().startOf('day').toISOString()},${moment().add(1, 'day').startOf('day').toISOString()}`
          valueFresh = CALCULATING
          if (typeof flowReady === 'undefined' || flowReady === null) {
            valueFresh = STATS_NOT_LOADED
          } else if (typeof flowReady === 'string' && flowReady === 'notLoaded') {
            valueFresh = CALCULATING
          } else if (typeof flowReady === 'string' && flowReady === 'loading') {
            valueFresh = CALCULATING
          } else if (
            typeof flowReady === 'string' && flowReady === 'loaded'
            // typeof statsLookup === 'object' &&
            // typeof statsLookup[hash] === 'object' &&
            // typeof statsLookup[hash].stats === 'object' &&
            // typeof statsLookup[hash].stats.sum !== 'undefined'
          ) {
            if (flowStats) {
              valueFresh = litersToUserPreference(flowStats.flow_rate, unitsOfFlowTitle, 'flow', 0)
            }
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}flow` +
                '?statType=gpm' +
                `&valueKey=${valueKey}` +
                '&precision=2' +
                '&period=7' +
                '&formula=litersToAcreFeet' +
                '&label=Flow+Rate+(' +
                `${unitsOfFlow}` +
                ')'}
              label={label}
              value={valueFresh}
              unit=''
            />
          )
          break
        case 'flowMeterCumulative':
          label = formatMessage({
            id: 'sensor.flow.totalAcreFeetCumulative',
            defaultMessage: `Cumulative (${volumeTitle})`,
            description: 'Cumulative Acre Feet Label',
          })
          valueKey = 'flowMeterSum'
          // hash = `${reading0.address},${valueKey},${moment().startOf('day').toISOString()},${moment().add(1, 'day').startOf('day').toISOString()}`
          valueFresh = CALCULATING
          if (typeof flowReady === 'undefined' || flowReady === null) {
            valueFresh = STATS_NOT_LOADED
          } else if (typeof flowReady === 'string' && flowReady === 'notLoaded') {
            valueFresh = CALCULATING
          } else if (typeof flowReady === 'string' && flowReady === 'loading') {
            valueFresh = CALCULATING
          } else if (
            typeof flowReady === 'string' && flowReady === 'loaded'
            // typeof statsLookup === 'object' &&
            // typeof statsLookup[hash] === 'object' &&
            // typeof statsLookup[hash].stats === 'object' &&
            // typeof statsLookup[hash].stats.sum !== 'undefined'
          ) {
            let flowTotal = 0
            dailySummary.forEach((i) => { flowTotal += i.flow_cumulative })
            valueFresh = litersToUserPreference(flowTotal, unitsOfVolume, 'volume', 2)
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}flow` +
                '?statType=cumulative' +
                `&valueKey=${valueKey}` +
                '&precision=2' +
                '&period=7' +
                '&formula=litersToAcreFeet' +
                '&label=Cumulative+(' +
                `${volumeTitle}` +
                ')'}
              label={label}
              value={valueFresh}
              unit=''
            />
          )
          break
        case 'precipitationDaily':
        { valueKey = '144'
          label = formatMessage({
            id: 'sensor.precipitation',
            defaultMessage: 'Precip',
            description: 'Precip In Label',
          })
          const hashPrecipitation = `${reading0.address},${valueKey},${moment().startOf('day').toISOString()},${moment().endOf('day').toISOString()}`
          valueFresh = CALCULATING
          const precipitationMultiplier = device.physical.precipitationMultiplier || 100
          const precipitationFormula = device.physical.precipitationFormula || 'inches'
          if (typeof statsLookup === 'undefined' || statsLookup === null) {
            valueFresh = STATS_NOT_LOADED
          } else if (typeof statsReady === 'string' && statsReady === 'notLoaded') {
            AppDispatcher.dispatch({
              action: 'get-stats-address',
              address: device.address,
              timeBeg: moment().subtract(1, 'days').format().substr(0, 10),
              timeEnd: moment().add(10, 'minutes').format().substr(0, 10),
              valueKey: '144',
            })
          } else if (typeof statsReady === 'string' && statsReady === 'loading') {
            valueFresh = CALCULATING
          } else if (
            typeof statsReady === 'string' && statsReady === 'loaded' &&
            typeof statsLookup === 'object' &&
            typeof statsLookup[hashPrecipitation] === 'object' &&
            typeof statsLookup[hashPrecipitation].stats === 'object' &&
            typeof statsLookup[hashPrecipitation].stats.sum !== 'undefined'
          ) {
            valueFresh = displayFormula(
              precipitationFormula,
              precipitationMultiplier,
              2,
              { tempConv, level },
              valueKey,
              { 144: statsLookup[hashPrecipitation].stats.sum },
              null,
              device.physical || {}
            )
          }
          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}stats` +
                '?statType=sum' +
                '&valueKey=144' +
                '&period=30' +
                `&multiplier=${precipitationMultiplier}` +
                '&precision=3' +
                `&formula=${precipitationFormula}` +
                `&label=${label.replace(' ', '+')}`}
              label={label}
              value={valueFresh}
              unit=''
            />
          )
          break }
        case 'settingAutoMode':
        case 'settingAutoModeAmarillo':
          telemetryTemporary = (
            <SettingAutoModeAmarillo
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingAutoModeAmarilloNoControl':
          telemetryTemporary = (
            <SettingAutoModeAmarillo
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={0} // 0 because we don't want to let the user control this
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingAutoModeHauff':
          telemetryTemporary = (
            <SettingAutoModeHauff
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingAutoModePump':
          telemetryTemporary = (
            <SettingAutoModePump
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingAutoModePump1':
          telemetryTemporary = (
            <SettingAutoModePump1
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
              number={1}
            />
          )
          break
        case 'settingAutoModePump2':
        case 'settingAutoModePump2_1':
          telemetryTemporary = (
            <SettingAutoModePump2
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
              number={1}
            />
          )
          break
        case 'settingAutoModePump2_2':
          telemetryTemporary = (
            <SettingAutoModePump2
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
              number={2}
            />
          )
          break
        case 'settingModbusStart':
          telemetryTemporary = (
            <SettingModbusStart
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingEngineState':
          telemetryTemporary = (
            <SettingEngineState
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingPumpState':
          telemetryTemporary = (
            <SettingPumpState
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingPumpState1':
          telemetryTemporary = (
            <SettingPumpState1
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
            />
          )
          break
        case 'settingPumpState2':
        case 'settingPumpState2_1':
          telemetryTemporary = (
            <SettingPumpState2
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
              number={1}
            />
          )
          break
        case 'settingPumpState2_2':
          telemetryTemporary = (
            <SettingPumpState2
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              device={device}
              status={status}
              level={level}
              customer={customer}
              pinCode={pinCode}
              number={2}
            />
          )
          break
        case 'settingSchedule':
        case 'settingSchedule_1':
          telemetryTemporary = (
            <IconLink
              key={`${path}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${path}settings/schedule/1`}
              label='Schedule'
              value={<i className='fa fa-calendar' style={{ margin: 'auto', fontSize: '4em' }} />}
              unit=''
              control
              color={settingStatus === 'new' && settingSchedules && Array.isArray(settingSchedules) && settingSchedules.find((item) => item.key === 1) ? colors.deviceProblem : colors.normal}
            />
          )
          break
        case 'settingSchedule_2':
          telemetryTemporary = (
            <IconLink
              key={`${path}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${path}settings/schedule/2`}
              label='Schedule'
              value={<i className='fa fa-calendar' style={{ margin: 'auto', fontSize: '4em' }} />}
              unit=''
              control
              color={settingStatus === 'new' && settingSchedules && Array.isArray(settingSchedules) && settingSchedules.find((item) => item.key === 2) ? colors.deviceProblem : colors.normal}
            />
          )
          break
        case 'settingTimeblocks':
        case 'settingTimeblocks_1':
          telemetryTemporary = (
            <IconLink
              key={`${path}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${path}settings/timeblocks/1`}
              label='Schedule'
              value={<i className='fa fa-calendar' style={{ margin: 'auto', fontSize: '4em' }} />}
              unit=''
              control
              color={settingStatus === 'new' && settingTimeblocks && settingTimeblocks['1'] ? colors.deviceProblem : colors.normal}
            />
          )
          break
        case 'settingTimeblocks_2':
          telemetryTemporary = (
            <IconLink
              key={`${path}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${path}settings/timeblocks/2`}
              label='Schedule'
              value={<i className='fa fa-calendar' style={{ margin: 'auto', fontSize: '4em' }} />}
              unit=''
              control
              color={settingStatus === 'new' && settingTimeblocks && settingTimeblocks['2'] ? colors.deviceProblem : colors.normal}
            />
          )
          break
        case 'settingTimeblocks_fertigation':
          telemetryTemporary = (
            <IconLink
              key={`${path}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${path}settings/timeblocks/fertigation`}
              label='Schedule'
              value={<i className='fa fa-calendar' style={{ margin: 'auto', fontSize: '4em' }} />}
              unit=''
              control
              color={settingStatus === 'new' && settingTimeblocks && (settingTimeblocks['1'] || settingTimeblocks['2']) ? colors.deviceProblem : colors.normal}
            />
          )
          break
        case 'timeSet':
          {
            const disabled = valveSchedules && Array.isArray(valveSchedules) && valveSchedules.length >= 160
            telemetryTemporary = (
              <IconLink
                key={`${path}${valueKey}${telemetryTypes[i]}`}
                name={`${telemetryTypes[i]}`}
                path={`${path}settings/timeSet/new`}
                label='Schedule'
                value={disabled ? 'OVER LIMIT' : <i className='fa fa-calendar' style={{ margin: 'auto', fontSize: '4em' }} />}
                unit=''
                control
                disabled={disabled}
                color={settingStatus === 'new' && valveSchedules && Array.isArray(valveSchedules) && valveSchedules.find((item) => item.key === 2) ? colors.deviceProblem : colors.normal}
              />
            )
          }
          break
        case 'diagnosticsRun':
          label = 'Run Diagnostics'
          valueKey = 134
          valueFresh = reading0[valueKey]
          unitFresh = ''

          if (status.id === 'notConnected') {
            valueFresh = '-'
            unitFresh = ''
          }

          telemetryTemporary = (
            <IconLink
              key={`${telPath}${valueKey}${telemetryTypes[i]}`}
              name={`${telemetryTypes[i]}`}
              path={`${telPath}generic` +
                '?valueKey=131,134,130,143,144,128,200,201,202' +
                '&multiplier=0.001,1,1,10,1,1,1,1,1' +
                '&formula=d,d,percentTo20V,d,d,temperature,temperature,temperature,d' +
                '&stacked=false' +
                '&label=Call+to+Run,RPM+Raw,Battery,Auto+Switch,MPC,Temperature,Temp+Start,Temp+Stop,Auto+Set'}
              label={label}
              value={valueFresh}
              unit={unitFresh}
            />
          )
          break
        default:
          break
      }
    }
    if (page === 'semios') {
      const { geom = '', property_id = '' } = physical
      const pageTemporary = (
        <IconLink
          key=''
          name='SemiosData'
          path={`https://live.semios.com/weather/${property_id}?geom=${geom}`}
          label='Semios Data'
          value=''
          unit=''
          icon={<Semios />}
          external
        />
      )
      telemetryReturn.push(pageTemporary)
    }

    telemetryReturn.push(telemetryTemporary)
  }

  const codeReturn = (
    <section>
      <div className={styles.tileContainer}>
        {telemetryReturn}
      </div>
    </section>
  )

  return codeReturn
}

TelemetryButtons.propTypes = {
  device: PropTypes.object,
  path: PropTypes.string,
  telemetryTypes: PropTypes.array,
  tempConv: PropTypes.string,
  level: PropTypes.number,
}

export default injectIntl(TelemetryButtons)
